import React, { useCallback, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Spinner from 'components/shared/Spinner';
import PaymentService from 'services/api/PaymentService';
import { renderPdfLoadingState } from 'util/Window';

interface UseTransactionPdfResult {
  openTransactionPdf: (transactionId: string, poNumber?: string) => Promise<boolean>;
  loadingComponent: React.ReactNode;
}

const useTransactionPdf = (): UseTransactionPdfResult => {
  const [loading, setLoading] = useState<boolean>(false);
  const theme = useTheme();

  const openTransactionPdf = useCallback(
    async (transactionId: string, poNumber?: string): Promise<boolean> => {
      const pdfWindow = window.open('about:blank', '_blank');

      if (!pdfWindow) {
        return false;
      }

      pdfWindow.opener = null;

      renderPdfLoadingState(pdfWindow, {
        backgroundColor: theme.palette.background.default,
        paperColor: theme.palette.background.paper,
        primaryColor: theme.palette.primary.main,
        textColor: theme.palette.text.primary,
        secondaryTextColor: theme.palette.text.secondary,
        dividerColor: theme.palette.divider,
        fontFamily: theme.typography.fontFamily,
        borderRadius: theme.shape.borderRadius,
        shadow: theme.shadows[4],
      });

      setLoading(true);

      try {
        const pdf = await PaymentService.getTransactionPdf(transactionId, poNumber);

        const pdfUrl = URL.createObjectURL(pdf);

        pdfWindow.location.replace(pdfUrl);

        return true;
      } catch (error) {
        pdfWindow.close();
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [theme]
  );

  return {
    openTransactionPdf,
    loadingComponent: <Spinner loading={loading} fullPage={true} />,
  };
};

export default useTransactionPdf;
